import { gatewayConfig } from "@courses-utils/gateway-auth/src/ts/config";

/**
 * Enumeration of different available publisher themes, when redirecting the
 * user to the LoginConnector.
 */
export enum LoginConnectorPublishers {
    GYLDENDAL_UDDANNELSE = "Gyldendal Uddannelse",
    HANS_REITZEL = "Hansreitzel",
    MUNKSGAARD = "Munksgaard",
}

/**
 * Specification of options available when redirecting the user to the
 * LoginConnector.
 */
type LoginConnectorOptions = {
    /**
     * Optionally specifies an absolute url, which the user will be redirected
     * to after having successfully signed in.
     */
    successUrl?: string;

    /**
     * Optionally specifies an absolute url, which the user will be redirected
     * to if something goes wrong during login.
     */
    failureUrl?: string;

    /**
     * If set to true, the user will be forced to re-login (even if he is
     * already signed in).
     */
    forceLogin?: boolean;

    /**
     * Optionally specify a specific publisher, whose theme should be used when
     * rendering the LoginConnector prompt.
     */
    publisherTheme?: LoginConnectorPublishers;
};

/**
 * Helper that redirects the user to the LoginConnector page, where the user
 * will be requested to sign in through one of the supported identity provides.
 *
 * @param clientName The name of the application/client to use to identify the
 *                   user within the LoginConnector.
 * @param options    Collection of options that'll be passed on to the actual
 *                   LoginConnector through querystrings.
 * @param returnUrl  If enabled, then the generated LoginConnector url will be
 *                   returned instead of redirecting the user there instantly.
 */
export function redirectToLoginConnector(profile: string, options: LoginConnectorOptions, returnUrl: true): string;
export function redirectToLoginConnector(profile: string, options?: LoginConnectorOptions): void;
export function redirectToLoginConnector(profile: string, options: LoginConnectorOptions = {}, returnUrl?: true): void | string {
    // We need to encode success and failure urls, to ensure that they're going
    // to be properly caught by the remote backend
    const failureUrl = encodeURIComponent(options.failureUrl || window.location.href);
    const successUrl = encodeURIComponent(options.successUrl || window.location.href);

    // Prepare the base url, which we're going to send the user to in order to
    // sign him in
    const url = [
        `${gatewayConfig.loginConnectorUrl}/Navigator/Navigator`,
        `?clientWebSite=${profile}`,
        `&clientWsSuccessUrl=${successUrl}`,
        `&clientWsFailureUrl=${failureUrl}`,
    ];

    // Push the forceLogin=true configuration if asked to do so
    if (options.forceLogin === true) {
        url.push("&forceLogin=true");
    }

    // Push publisher theme to url, if a custom one was specified
    if (options.publisherTheme) {
        url.push(`&onBehalfOf=${encodeURIComponent(options.publisherTheme)}`);
    }

    // Now we just need to actually redirect the user to the LoginConnector
    if (!returnUrl) {
        window.location.href = url.join("");
    } else {
        return url.join("");
    }
}
